
import { Box, Button, Paper, Typography, TextField, MenuItem, IconButton, Autocomplete } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from '../../../styles.module.css';
import { Breadcrumb, Cancel, CTSinput } from "../../reusableComponent/Reusables";
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionIcon from '@mui/icons-material/Description';
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import plantMasterService from "../../../services/masters/plantMaster.Service";

// ── CSS Styles ─────────────────────────────────────────────────────────────
// 1. Page wrapper
const pageSx = {
  background: "#F4F7FB",
  minHeight: "100vh",
  padding: "20px 24px 40px",
  fontFamily: "'Inter', Arial, sans-serif",
};

// 2. Breadcrumb
const breadcrumbSx = {
  fontSize: "12px",
  color: "#718096",
  mb: 2,
  display: "flex",
  alignItems: "center",
  gap: "6px",
};

// 3. Page Header row
const pageHeaderSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  mb: 2.5,
};

// Page title
const pageTitleSx = {
  fontSize: "16px",
  fontWeight: 700,
  color: "#000000",
  display: "flex",
  alignItems: "center",
  gap: "10px",
};

// Back button
const backBtnSx = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  fontSize: "13px",
  fontWeight: 600,
  color: "#2B6CB0",
  padding: "7px 14px",
  border: "1.5px solid #2B6CB0",
  borderRadius: "7px",
  background: "#fff",
  cursor: "pointer",
  textTransform: "none",
  "&:hover": { background: "#E6F1FB" },
};

// 4. Main form card
const formCardSx = {
  background: "#fff",

  border: "1px solid #E2E8F0",
  overflow: "hidden",
};

// 5. Section header bar (navy strip at top)
const sectionHeaderSx = {
  background: "#1C3A5E",
  padding: "12px 22px",
  display: "flex",
  alignItems: "center",
  gap: "10px",
};

const sectionHeaderTitleSx = {
  fontSize: "13px",
  fontWeight: 600,
  color: "#fff",
  letterSpacing: "0.3px",
};

// 6. Form body padding
const formBodySx = { padding: "22px 24px" };

// 7. Field label
const fieldLabelSx = {
  fontSize: "12px",
  fontWeight: 600,
  color: "#2063b6",
  mb: "4px",
  display: "flex",
  alignItems: "center",
  gap: "4px",
};

// 8. MUI TextField override
const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "13px",
    fontFamily: "'Inter', Arial, sans-serif",
    background: "#fafafa",
    height: "38px",
    "& fieldset": { borderColor: "#E2E8F0", borderWidth: "1.5px" },
    "&:hover fieldset": { borderColor: "#CBD5E0" },
    "&.Mui-focused fieldset": { borderColor: "#2B6CB0", borderWidth: "2px" },
    "&.Mui-focused": { background: "#fff" },
    "&.Mui-disabled": { background: "#F8FAFC" },
  },
  "& input": {
    color: "#1A202C",
    fontSize: "13px",
    fontFamily: "'Inter', Arial, sans-serif",
    "&::placeholder": { color: "#A0AEC0", opacity: 1 },
  },
  "& .MuiInputLabel-root": { display: "none" },
};

// 9. MenuItem style
const menuItemSx = { fontSize: "13px", fontFamily: "'Inter', Arial, sans-serif" };

// 10. Form footer
const formFooterSx = {
  padding: "18px 24px",
  background: "#F8FAFC",
  borderTop: "1px solid #E2E8F0",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: "10px",
};

// 11. Save button
const saveBtnSx = {
  display: "flex",
  alignItems: "center",
  gap: "7px",
  padding: "0 22px",
  height: "40px",
  background: "#4CAF50",
  color: "#fff",
  border: "none",
  fontSize: "14px",
  fontWeight: 600,
  textTransform: "none",
  fontFamily: "'Inter', Arial, sans-serif",
  "&:hover": { background: "#028b19", transform: "translateY(-1px)" },
  "&:active": { transform: "translateY(0)" },
  "&.Mui-disabled": { background: "#94a3b8", color: "#fff" },
  transition: "all 0.15s ease",
};

// 12. Cancel button
const cancelBtnSx = {
  display: "flex",
  alignItems: "center",
  gap: "7px",
  padding: "0 22px",
  height: "40px",
  background: "#E53E3E",
  color: "#ffffff",
  border: "none",
  "&:hover": { background: "#971f16", transform: "translateY(-1px)" },
  fontSize: "14px",
  fontWeight: 600,
  borderRadius: "8px",
};

// ─────────────────────────────────────────────────────────────────────────────

const PlantAdd = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const urlLocation = useLocation();
    const editData = urlLocation?.state;

    const login_details = sessionStorage.getItem('loginUser');
    const userD = JSON.parse(login_details);
    const userInfo = userD.result.userInfo;

    const [data, setData] = useState({
        plant_name: '',
        plant_address: '',
        plant_manager: '',
        acetone_stock: '',
        carbide_stock: '',
        mobile_no: '',
        plant_id: '',
        short_code: '',
        carbide_safety_stock: '',
        carbide_opening_stock: '',
        acetone_safety_stock: '',
        acetone_opening_stock: '',
    });

    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    // Prefill state if editing
    useEffect(() => {
        if (editData) {
            setData({
                plant_name: editData.plant_name || '',
                plant_address: editData.plant_address || '',
                plant_manager: editData.plant_manager || '',
                mobile_no: editData.mobile_no || '',
                plant_id: editData.plant_id || '',
                short_code: editData.short_code || '',
                carbide_stock: editData.carbide_stock || '',
                carbide_safety_stock: editData.carbide_safety_stock || '',
                carbide_opening_stock: editData.carbide_opening_stock || '',
                acetone_stock: editData.acetone_stock || '',
                acetone_safety_stock: editData.acetone_safety_stock || '',
                acetone_opening_stock: editData.acetone_opening_stock || '',
            });
        }
    }, [editData]);

    const handleChange = ({ target }) => {
        const { name, value } = target;
        setData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const validateForm = () => {
        if (!data.plant_name || !data.mobile_no) {
            setErrorMessage("Plant name and mobile number are required.");
            return false;
        }
        return true;
    };

    const onSubmit = async (e) => {
        e.preventDefault();

        setErrorMessage('');
        if (!validateForm()) return;

        const plantData = {
            ...data,
            modified_at: dayjs().format('YYYY-MM-DD HH:mm:ss'),
            modified_by: editData ? userInfo.user_id : null,
            created_at: dayjs().format('YYYY-MM-DD HH:mm:ss'),
            created_by: editData ? editData.created_by : userInfo.user_id,
            changedUpdatedValue: 'edit',
        };

        try {
            if (editData) {
                await plantMasterService.editDeletePlant(plantData);
            } else {
                await plantMasterService.addPlant(plantData);
            }

            setSuccessMessage(editData ? "Updated successfully" : "Added successfully");
            setTimeout(() => navigate(-1), 1000);
        } catch (error) {
            console.error("Submission error:", error);
            setErrorMessage("Error occurred while saving data.");
        }
    };

    const handleBack = () => {
        navigate(-1);
    };

    return (
        <Box sx={pageSx}>
            {/* Breadcrumb */}
            <Box sx={breadcrumbSx}>
                <span>Dashboard</span> / <span style={{ color: "#2B6CB0", fontWeight: 600 }}>Plant</span>
            </Box>

            {/* Page Header */}
            <Box sx={pageHeaderSx}>
                <Box sx={pageTitleSx}>
                    <DescriptionIcon sx={{ fontSize: 20, color: "#1C3A5E" }} />
                    {editData ? 'UPDATE' : 'ADD'} PLANT MASTER
                </Box>
                <Button onClick={handleBack} sx={backBtnSx}>
                    <ArrowBackIcon sx={{ fontSize: 16 }} /> Back
                </Button>
            </Box>

            {/* Main Form Card */}
            <Box sx={formCardSx}>
                {/* Navy Strip Header */}
                <Box sx={sectionHeaderSx}>
                    <DescriptionIcon sx={{ fontSize: 18, color: "#fff" }} />
                    <Typography sx={sectionHeaderTitleSx}>Plant Master</Typography>
                </Box>

                <form onSubmit={onSubmit}>
                    <Box sx={formBodySx}>
                        <Grid container spacing={3}>
                            {/* Row 1 */}
                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>
                                        Plant Name <span style={{ color: "#E53E3E", fontSize: "13px" }}>*</span>
                                    </Typography>
                                    <TextField
                                        size='small'
                                        fullWidth
                                        type="text"
                                        onChange={handleChange}
                                        name="plant_name"
                                        value={data.plant_name}
                                        sx={inputSx}
                                        placeholder="Enter plant name"
                                        required
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>Carbide Stock</Typography>
                                    <TextField
                                        size='small'
                                        fullWidth
                                        type="text"
                                        onChange={handleChange}
                                        name="carbide_stock"
                                        value={data.carbide_stock}
                                        sx={inputSx}
                                        placeholder="Enter carbide stock"
                                    />
                                </Box>
                            </Grid>

                            {/* Row 2 */}
                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>Plant Address</Typography>
                                    <TextField
                                        size='small'
                                        fullWidth
                                        type="text"
                                        onChange={handleChange}
                                        name="plant_address"
                                        value={data.plant_address}
                                        sx={inputSx}
                                        placeholder="Enter plant address"
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>Acetone Stock</Typography>
                                    <TextField
                                        size='small'
                                        fullWidth
                                        type="text"
                                        onChange={handleChange}
                                        name="acetone_stock"
                                        value={data.acetone_stock}
                                        sx={inputSx}
                                        placeholder="Enter acetone stock"
                                    />
                                </Box>
                            </Grid>

                            {/* Row 3 */}
                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>Plant Manager</Typography>
                                    <TextField
                                        size='small'
                                        fullWidth
                                        type="text"
                                        onChange={handleChange}
                                        name="plant_manager"
                                        value={data.plant_manager}
                                        sx={inputSx}
                                        placeholder="Enter plant manager"
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>Short Code</Typography>
                                    <TextField
                                        size='small'
                                        fullWidth
                                        type="text"
                                        onChange={handleChange}
                                        name="short_code"
                                        value={data.short_code}
                                        sx={inputSx}
                                        placeholder="Enter short code"
                                    />
                                </Box>
                            </Grid>

                            {/* Row 4 */}
                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>
                                        Mobile No <span style={{ color: "#E53E3E", fontSize: "13px" }}>*</span>
                                    </Typography>
                                    <TextField
                                        size='small'
                                        fullWidth
                                        type="text"
                                        onChange={handleChange}
                                        name="mobile_no"
                                        value={data.mobile_no}
                                        sx={inputSx}
                                        placeholder="Enter mobile number"
                                        required
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>Carbide Safety Stock</Typography>
                                    <TextField
                                        size='small'
                                        fullWidth
                                        type="text"
                                        onChange={handleChange}
                                        name="carbide_safety_stock"
                                        value={data.carbide_safety_stock}
                                        sx={inputSx}
                                        placeholder="Enter carbide safety stock"
                                    />
                                </Box>
                            </Grid>

                            {/* Row 5 */}
                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>Carbide Opening Stock</Typography>
                                    <TextField
                                        size='small'
                                        fullWidth
                                        type="text"
                                        onChange={handleChange}
                                        name="carbide_opening_stock"
                                        value={data.carbide_opening_stock}
                                        sx={inputSx}
                                        placeholder="Enter carbide opening stock"
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>Acetone Safety Stock</Typography>
                                    <TextField
                                        size='small'
                                        fullWidth
                                        type="text"
                                        onChange={handleChange}
                                        name="acetone_safety_stock"
                                        value={data.acetone_safety_stock}
                                        sx={inputSx}
                                        placeholder="Enter acetone safety stock"
                                    />
                                </Box>
                            </Grid>

                            {/* Row 6 */}
                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography sx={fieldLabelSx}>Acetone Opening Stock</Typography>
                                    <TextField
                                        size='small'
                                        fullWidth
                                        type="text"
                                        onChange={handleChange}
                                        name="acetone_opening_stock"
                                        value={data.acetone_opening_stock}
                                        sx={inputSx}
                                        placeholder="Enter acetone opening stock"
                                    />
                                </Box>
                            </Grid>

                            {/* Success/Error Messages */}
                            {successMessage && (
                                <Grid item xs={12}>
                                    <Typography sx={{ color: "#4CAF50", fontSize: "14px", mt: 1 }}>{successMessage}</Typography>
                                </Grid>
                            )}
                            {errorMessage && (
                                <Grid item xs={12}>
                                    <Typography sx={{ color: "#E53E3E", fontSize: "14px", mt: 1 }}>{errorMessage}</Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Box>

                    {/* Form Footer */}
                    <Box sx={formFooterSx}>
                        <Button sx={cancelBtnSx} onClick={handleBack}>
                            Cancel
                        </Button>
                        <Button type="submit" sx={saveBtnSx}>
                            <SaveIcon sx={{ fontSize: 18 }} />
                            {editData ? "Update" : "Save"}
                        </Button>
                    </Box>
                </form>
            </Box>
        </Box>
    );
};

export default PlantAdd;